import { type JSX, Match, Switch } from 'solid-js';
import { t } from '../i18n';
import { machineDiagnosis } from '../lib/fleet-machine-panel';
import type { UtilBox } from '../lib/fleetUtilization';
import { MACHINE_HEALTH } from '../lib/machine-labels';
import { decimal } from '../lib/time';
import { Button } from './ui/Button';

// The details panel's diagnosis (#6563, prototype `panel()` "What is slowing it down"):
// what is wrong and what helps, per `machineDiagnosis` (`lib/fleet-machine-panel.ts`).
// An overloaded machine names its CPU ratio; when other machines on its server are
// overloaded too, the server itself is short on processors, and "View server" groups the
// list by server. "Memory and disk are fine." is said only when both are.

export function MachineDiagnosis(props: {
  readonly box: UtilBox;
  readonly boxes: readonly UtilBox[];
  readonly onViewServer: () => void;
}): JSX.Element {
  const diagnosis = () => machineDiagnosis(props.box, props.boxes);
  const overloaded = () => {
    const d = diagnosis();
    return d.kind === 'overloaded' ? d : null;
  };
  return (
    <Switch>
      <Match when={overloaded()}>
        {(d) => (
          <section class="machine-panel__diag" aria-label={t('fleet.panel.diag_heading')}>
            <h3 class="machine-panel__diag-heading" data-tone="crit">
              {t('fleet.panel.diag_heading')}
            </h3>
            <p class="machine-panel__diag-text">
              <strong>
                {d().ratio === null
                  ? t('fleet.panel.diag_cpu_plain')
                  : t('fleet.panel.diag_cpu', { ratio: decimal(d().ratio ?? 0) })}
              </strong>{' '}
              <span class="machine-panel__muted">
                {d().othersFine ? `${t('fleet.panel.diag_others_fine')} ` : ''}
                {d().server === null
                  ? t('fleet.panel.diag_alone')
                  : t('fleet.panel.diag_server', { server: d().server ?? '' })}
              </span>
            </p>
            {d().server !== null && (
              <Button variant="link" size="sm" onClick={() => props.onViewServer()}>
                {t('fleet.panel.view_server', { server: d().server ?? '' })}
              </Button>
            )}
          </section>
        )}
      </Match>
      <Match when={diagnosis().kind === 'attention'}>
        <section class="machine-panel__diag" aria-label={t('fleet.panel.diag_look_heading')}>
          <h3 class="machine-panel__diag-heading" data-tone="warn">
            {t('fleet.panel.diag_look_heading')}
          </h3>
          <p class="machine-panel__diag-text">{t(MACHINE_HEALTH[props.box.verdict.health].key)}</p>
        </section>
      </Match>
      <Match when={diagnosis().kind === 'fine'}>
        <p class="machine-panel__fine">{t('fleet.panel.diag_fine')}</p>
      </Match>
    </Switch>
  );
}
